import React, { ReactNode, useState, useEffect } from 'react';
import { VscLinkExternal } from 'react-icons/vsc';
import {
  Flex,
  Text,
  Wrap,
  Button,
  HStack,
  FlexProps,
  VStack,
  IconButton,
  Tooltip,
} from '@chakra-ui/react';
import { DateRangeComp, ErrorCard, SegmentedPicker } from 'components';
import { useDateSelect } from 'hooks/useDateSelect';
import { useSegmentedPicker } from 'hooks/useSegmentedPicker';
import { DETAILS_LABEL } from 'utils/constants';
import { Card } from './Card';

interface ChartCard extends FlexProps {
  title?: string;
  customTitle?: ReactNode;
  subtitleMb?: string | number;
  subtitle?: string;
  hasFilter?: boolean;
  filters?: ChartTypeFilter[];
  setFilter?: React.Dispatch<React.SetStateAction<ChartTypeFilter>>;
  picker?: string[];
  pickerType?: string;
  setPicked?: React.Dispatch<React.SetStateAction<string>>;
  firstPicked?: string;
  setDateRange?: React.Dispatch<React.SetStateAction<DateProps[]>>;
  startDate?: DateProps[];
  details?: () => void;
  children: React.ReactNode;
  from?: string;
  hasDate?: boolean;
  cardName?: string;
  isError?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  refetch?: any;
}

export const ChartCard: React.FC<ChartCard> = ({
  title,
  customTitle,
  subtitleMb,
  details,
  subtitle,
  children,
  picker,
  pickerType,
  filters,
  setFilter,
  setPicked,
  firstPicked,
  from,
  setDateRange,
  startDate,
  cardName,
  isError,
  refetch,
  ...rest
}) => {
  const { onDateRangeChange } = useDateSelect(setDateRange);
  const { onSegmentedPickerChange } = useSegmentedPicker(setPicked);

  const [selectedFilter, setSelectedFilter] = useState<ChartTypeFilter>(
    filters?.[0] || { label: '', icon: null, type: '' },
  );

  useEffect(() => {
    if (setFilter) setFilter(selectedFilter);
  }, [selectedFilter]);

  if (isError)
    return <ErrorCard {...rest} refetch={() => (refetch ? refetch() : null)} />;

  return (
    <Card {...rest}>
      <Flex justifyContent="space-between" justifySelf="flex-start">
        {customTitle ? (
          customTitle
        ) : (
          <Text
            alignSelf="center"
            color="text.100"
            fontSize="16px"
            fontWeight={400}
          >
            {title}
          </Text>
        )}
        <Flex>
          <HStack
            ml="8px"
            spacing="22px"
            display={
              from !== 'home'
                ? { base: 'none', '2xl': 'flex' }
                : { base: 'none', xl: 'flex' }
            }
          >
            {filters && setFilter && (
              <HStack spacing="18px">
                {filters.map((filter, index) => (
                  <Tooltip
                    key={index}
                    label={filter.label}
                    bg="button2"
                    color="white"
                    borderRadius="4px"
                  >
                    <IconButton
                      color="button2"
                      bg="transparent"
                      p={1}
                      _active={{ color: 'white', bg: 'button2' }}
                      _hover={{ color: 'white', bg: 'button2' }}
                      isActive={selectedFilter === filter}
                      aria-label={filter.label}
                      size=""
                      icon={React.createElement(filter.icon, {
                        size: '14px',
                      })}
                      onClick={() => setSelectedFilter(filter)}
                    />
                  </Tooltip>
                ))}
              </HStack>
            )}

            {setDateRange && (
              <DateRangeComp
                type={pickerType === 'date' ? firstPicked : 'month'}
                startDate={startDate}
                ref={onDateRangeChange}
                minW="170px"
              />
            )}

            {!!picker && setPicked && (
              <SegmentedPicker
                firstSelectedLabel={firstPicked}
                ref={onSegmentedPickerChange}
                labels={picker}
              />
            )}
          </HStack>
          {!!details && (
            <Button
              data-cy={`details-${cardName}`}
              onClick={details}
              variant="link"
              rightIcon={<VscLinkExternal />}
              ml="22px"
            >
              {DETAILS_LABEL}
            </Button>
          )}
        </Flex>
      </Flex>
      <Text
        textAlign="start"
        mb={{ base: '18px', xl: subtitleMb || '35px' }}
        color="text.300"
        fontSize="12px"
        fontWeight={500}
      >
        {subtitle || ''}
      </Text>

      <VStack
        display={
          from !== 'home'
            ? { base: 'flex', '2xl': 'none' }
            : { base: 'flex', xl: 'none' }
        }
        align="start"
        spacing="18px"
        mb="20px"
      >
        {filters && setFilter && (
          <HStack spacing="14px">
            {filters.map((filter, index) => (
              <Button
                whiteSpace="unset"
                key={index}
                variant="ghost"
                flexDir="column"
                leftIcon={React.createElement(filter.icon, {
                  size: '18px',
                  style: { marginBottom: '8px', marginLeft: '8px' },
                })}
                iconSpacing="8px"
                w="12%"
                px={1}
                textColor={selectedFilter === filter ? 'text.100' : 'text.200'}
                _hover={{ textColor: 'text.100' }}
                _focus={{ bg: 'none' }}
                fontSize="10px"
                onClick={() => setSelectedFilter(filter)}
              >
                {filter.label}
              </Button>
            ))}
          </HStack>
        )}
        <Wrap align="center">
          {setDateRange && (
            <DateRangeComp
              dateName={cardName}
              type={pickerType === 'date' ? firstPicked : 'month'}
              startDate={startDate}
              ref={onDateRangeChange}
              minW="186"
            />
          )}
          {!!picker && setPicked && (
            <SegmentedPicker
              pickerName={cardName}
              firstSelectedLabel={firstPicked}
              ref={onSegmentedPickerChange}
              labels={picker}
            />
          )}
        </Wrap>
      </VStack>
      <Flex flex={1} />
      <Wrap spacing="47px">{children}</Wrap>
    </Card>
  );
};
